/**
 * Partial application drafts: browser → API only (never Zoho Forms directly).
 * Server upserts by email and syncs one Zoho Forms row — see docs/100BM-form-partial-zoho.md
 */

const FORM_PARTIAL_ENDPOINT =
  'https://api.iamironlady.com/api/form-partial?key=10e7178a893d0c0f6444ae687f3f8a1416a57dfb6dab4bd2';

export const FORM_IDS = {
  application: '100BMApplicationWEB',
  briefing: '100BMExecutiveBriefingWeb',
};

const API_HASH_PREFIX = 'il_form_partial_hash_';
const PENDING_STORAGE_KEY = 'il_form_partial_pending_v1';
const PENDING_MAX = 25;

const EXPERIENCE_LABELS = {
  '8-10': '8-10 years',
  '11-15': '11-15 years',
  '15+': '15+ years',
};

const SCOPE_LABELS = {
  individual: 'Individual contributor',
  'small-team': 'Small team',
  'team-function': 'Leading team/function',
  'cross-functional': 'Cross-functional',
  pnl: 'P&L accountability',
  others: 'Others',
};

const BRIEFING_SOURCE_LABELS = {
  'instagram-linkedin': 'Instagram/LinkedIn',
  referral: 'Referral',
  'google-search': 'Google/Search',
  community: 'Community',
  other: 'Other',
};

/** @param {string} value */
function mapIncomeRange(value) {
  const v = (value || '').trim();
  const map = {
    '<10L': 'Less than 10L',
    'Less than 10L': 'Less than 10L',
    '10-20L': '10L - 20L',
    '10L-20L': '10L - 20L',
    '10L - 20L': '10L - 20L',
    '20-30L': '20L - 30L',
    '20L-30L': '20L - 30L',
    '20L - 30L': '20L - 30L',
    '30-50L': '30L - 50L',
    '30L-50L': '30L - 50L',
    '30L - 50L': '30L - 50L',
    '50L-1Cr': '50L - 1Cr',
    '50L - 1Cr': '50L - 1Cr',
    '>1Cr': 'Above 1Cr',
    'Above 1Cr': 'Above 1Cr',
  };
  return map[v] || v;
}

export function captureUtms() {
  let stored = {};
  try {
    stored = JSON.parse(sessionStorage.getItem('il_utm') || '{}');
  } catch (e) {
    stored = {};
  }
  const q = new URLSearchParams(window.location.search);
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((k) => {
    if (q.get(k)) stored[k] = q.get(k);
  });
  try {
    sessionStorage.setItem('il_utm', JSON.stringify(stored));
  } catch (e) {
    /* ignore */
  }
  return stored;
}

export function partialFieldsHash(fields) {
  return Object.keys(fields)
    .sort()
    .map((k) => `${k}=${fields[k]}`)
    .join('|');
}

function hashStorageKey(formId, email) {
  return `${API_HASH_PREFIX}${formId}_${(email || '').trim().toLowerCase()}`;
}

function readStoredHash(formId, email) {
  try {
    return sessionStorage.getItem(hashStorageKey(formId, email)) || '';
  } catch (e) {
    return '';
  }
}

function writeStoredHash(formId, email, hash) {
  try {
    sessionStorage.setItem(hashStorageKey(formId, email), hash);
  } catch (e) {
    /* ignore */
  }
}

export function clearPartialDraftHash(formId, email) {
  const em = (email || '').trim().toLowerCase();
  if (!em) return;
  try {
    sessionStorage.removeItem(hashStorageKey(formId, em));
  } catch (e) {
    /* ignore */
  }
}

export function isChaseableFields(fields) {
  const email = (fields.Email || '').trim();
  const okEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const okPhone = (fields.Phone || '').replace(/\D/g, '').length >= 8;
  return okEmail || okPhone;
}

/** React application modal snapshot → API field names (same as 100bm-apply.html). */
export function applicationSnapshotToFields(data) {
  const f = {};
  const name = (data.fullName || '').trim();
  if (name) f.Name = name;
  if (data.email?.trim()) f.Email = data.email.trim();
  if (data.phone?.trim()) f.Phone = data.phone.trim();
  if (data.designation?.trim()) f.Designation = data.designation.trim();
  if (data.organisation?.trim()) f.Organisation = data.organisation.trim();
  if (data.industry?.trim()) f.Industry = data.industry.trim();
  if (data.experience) {
    f.Experience = EXPERIENCE_LABELS[data.experience] || data.experience;
  }
  if (data.income) f['Income Range'] = mapIncomeRange(data.income);
  if (data.scope) f['Scope of Responsibility'] = SCOPE_LABELS[data.scope] || data.scope;
  if (data.q7?.trim()) f['Why Applying'] = data.q7.trim();
  if (data.q8?.trim()) f['3-Year Vision'] = data.q8.trim();
  if (data.q9?.trim()) f['Top Challenges'] = data.q9.trim();
  return f;
}

/** React briefing modal snapshot → API fields. */
export function briefingSnapshotToFields(data) {
  const f = {};
  const name = (data.fullName || '').trim();
  if (name) f.Name = name;
  if (data.email?.trim()) f.Email = data.email.trim();
  if (data.phone?.trim()) f.Phone = data.phone.trim();
  if (data.city?.trim()) f.City = data.city.trim();
  if (data.designation?.trim()) f.Designation = data.designation.trim();
  if (data.industry?.trim()) f.Industry = data.industry.trim();
  if (data.experience) {
    f.Experience = EXPERIENCE_LABELS[data.experience] || data.experience;
  }
  if (data.income) f['Income Range'] = mapIncomeRange(data.income);
  if (data.objectives?.trim()) f.Objectives = data.objectives.trim();
  if (data.source) {
    f.Source = BRIEFING_SOURCE_LABELS[data.source] || data.source;
  }
  return f;
}

function buildPayload({ formId, fields, step, page, partial, finalSubmit, deletePartial }) {
  return {
    formId,
    fields,
    utm: captureUtms(),
    step: step ?? 1,
    page: page || window.location.href,
    ts: new Date().toISOString(),
    partial: partial !== false,
    ...(finalSubmit ? { final: true } : {}),
    ...(deletePartial ? { deletePartial: true } : {}),
  };
}

function readPendingQueue() {
  try {
    const raw = localStorage.getItem(PENDING_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function writePendingQueue(items) {
  try {
    localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(items.slice(-PENDING_MAX)));
  } catch (e) {
    /* ignore */
  }
}

function pendingKey(body) {
  const email = (body.fields?.Email || '').trim().toLowerCase();
  return `${body.formId || ''}:${email}`;
}

export function enqueuePartialPayload(body) {
  if (!body?.fields) return;
  const key = pendingKey(body);
  const items = readPendingQueue().filter((item) => pendingKey(item) !== key);
  items.push({ ...body, queuedAt: new Date().toISOString() });
  writePendingQueue(items);
}

function removePendingPayload(body) {
  const key = pendingKey(body);
  writePendingQueue(readPendingQueue().filter((item) => pendingKey(item) !== key));
}

/** Retry API saves that failed earlier (e.g. API was down). */
export async function flushPendingPartials() {
  const items = readPendingQueue();
  if (!items.length) return { flushed: 0, remaining: 0 };
  const remaining = [];
  let flushed = 0;
  for (const body of items) {
    try {
      const res = await fetch(FORM_PARTIAL_ENDPOINT, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        remaining.push(body);
        continue;
      }
      flushed += 1;
      const email = (body.fields?.Email || '').trim().toLowerCase();
      const formId = body.formId;
      if (email && formId) {
        writeStoredHash(formId, email, partialFieldsHash(body.fields || {}));
      }
    } catch (e) {
      remaining.push(body);
    }
  }
  writePendingQueue(remaining);
  return { flushed, remaining: remaining.length };
}

async function runEmergencyZohoPartial(formType, fields) {
  try {
    const { submitPartialEmergencyToZoho } = await import('./zohoFormService');
    return submitPartialEmergencyToZoho(formType, fields);
  } catch (e) {
    return false;
  }
}

/**
 * Save partial draft (close tab / modal). Skips unchanged payload hash per email.
 * On API failure: queue in localStorage; on tab close also try emergency Zoho POST.
 */
export async function submitPartialDraft({
  formType,
  fields,
  step = 1,
  page,
  useBeacon = false,
  allowEmergencyZoho = true,
}) {
  const formId = FORM_IDS[formType];
  if (!formId || !fields || !isChaseableFields(fields)) {
    return { skipped: true };
  }
  const email = (fields.Email || '').trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { skipped: true };
  }
  const hash = partialFieldsHash(fields);
  if (hash === readStoredHash(formId, email)) {
    return { skipped: true, reason: 'unchanged' };
  }
  const body = buildPayload({
    formId,
    fields,
    step,
    page,
    partial: true,
  });
  const json = JSON.stringify(body);

  if (useBeacon && navigator.sendBeacon) {
    const blob = new Blob([json], { type: 'application/json' });
    const beaconOk = navigator.sendBeacon(FORM_PARTIAL_ENDPOINT, blob);
    if (beaconOk) {
      writeStoredHash(formId, email, hash);
      removePendingPayload(body);
      return { sent: true, via: 'beacon' };
    }
  }

  try {
    const res = await fetch(FORM_PARTIAL_ENDPOINT, {
      method: 'POST',
      body: json,
      keepalive: useBeacon,
    });
    if (res.ok) {
      writeStoredHash(formId, email, hash);
      removePendingPayload(body);
      return { sent: true, via: 'fetch' };
    }
  } catch (e) {
    /* queue below */
  }

  enqueuePartialPayload(body);
  let emergencyZoho = false;
  if (allowEmergencyZoho && useBeacon) {
    emergencyZoho = await runEmergencyZohoPartial(formType, fields);
  }
  return { sent: false, queued: true, emergencyZoho };
}

export function submitPartialBeacon(formType, fields, step = 1) {
  void submitPartialDraft({
    formType,
    fields,
    step,
    useBeacon: true,
  });
}

/** Full application via API (primary path for 100bm-apply.html and React). */
export async function submitFinalDraft({
  formType,
  fields,
  step = 3,
  page,
}) {
  const formId = FORM_IDS[formType];
  if (!formId || !fields) {
    throw new Error('Invalid form submission');
  }
  const email = (fields.Email || '').trim().toLowerCase();
  clearPartialDraftHash(formId, email);
  const body = buildPayload({
    formId,
    fields,
    step,
    page,
    partial: false,
    finalSubmit: true,
    deletePartial: true,
  });
  const res = await fetch(FORM_PARTIAL_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`http ${res.status}`);
  removePendingPayload(body);
  return res.json();
}
